import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const prettyKey = (key) => {
  const map = {
    dns_mx: 'DNS: MX Record',
    ssl_webmail: 'SSL: Webmail',
    ssl_mail_proxy: 'SSL: Mail-Proxy',
    http_website: 'HTTP: Website',
    http_webmail: 'HTTP: Webmail',
    dns_webmail_points_to_us: 'DNS: webmail -> unsere IP',
  };
  return map[key] || key;
};

const statusBadge = (ok) => {
  if (ok) return <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">OK</Badge>;
  return <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 text-xs">Fail</Badge>;
};

const toPairs = (obj) => {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).filter(([k, v]) => k !== 'ok' && k !== 'points' && v !== null && v !== undefined && v !== '');
};

export const DomainHealthDetailsDialog = ({ open, onOpenChange, domain }) => {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);

  const title = useMemo(() => {
    const label = data?.label ? ` — ${data.label}` : '';
    return `${domain || ''}${label}`;
  }, [domain, data?.label]);

  const fetchDetail = useCallback(async (force) => {
    if (!domain) return;
    try {
      const api = authAxios();
      const qs = force ? '?refresh=true' : '';
      const res = await api.get(`/domains/health/${encodeURIComponent(domain)}${qs}`);
      setData(res.data?.item || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Details konnten nicht geladen werden');
      setData(null);
    }
  }, [authAxios, domain]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchDetail(false).finally(() => setLoading(false));
  }, [open, fetchDetail]);

  const checks = data?.checks || {};
  const keys = Object.keys(checks);
  keys.sort();

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDetail(true);
    setRefreshing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-[#a1a1aa]">
            Score: <span className="text-white font-medium">{data?.score ?? '-'}</span>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCw className={refreshing ? 'animate-spin' : ''} />
            Jetzt prüfen
          </Button>
        </div>

        <ScrollArea className="h-[420px] pr-3">
          {loading ? (
            <div className="py-8 text-sm text-[#a1a1aa]">Lade Checks…</div>
          ) : !data ? (
            <div className="py-8 text-sm text-[#a1a1aa]">Keine Daten</div>
          ) : (
            <div className="space-y-3">
              {keys.map((k) => {
                const c = checks[k];
                const ok = Boolean(c?.ok);
                const pts = c?.points ?? 0;
                const pairs = toPairs(c);

                return (
                  <div key={k} className="rounded-lg border border-white/10 bg-black/20 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          {ok ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-rose-400" />
                          )}
                          <div className="font-medium text-white truncate">{prettyKey(k)}</div>
                        </div>
                        {pairs.length > 0 && (
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#a1a1aa]">
                            {pairs.map(([pk, pv]) => (
                              <div key={pk} className="flex items-center justify-between gap-3">
                                <span className="text-[#71717a]">{pk}</span>
                                <span className="text-white/90 truncate">{String(pv)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {statusBadge(ok)}
                        <Badge className="bg-white/5 text-[#a1a1aa] border-white/10 text-xs">{pts}p</Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
